import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, KeyRound, Trash2, Loader2, X, Users, ShieldCheck, Pencil } from "lucide-react";

const ROLES = ["admin", "editor", "viewer"];
const ROLE_STYLE = {
  admin: "bg-gold text-navy",
  editor: "bg-navy/10 text-navy",
  viewer: "bg-navy/5 text-navy/70",
};

const ROLE_DESC = {
  admin: "Full access — can manage users, publish, edit, and delete anything.",
  editor: "Can create/edit content but cannot manage users or delete sensitive records.",
  viewer: "Read-only access to admin dashboards.",
};

export default function AdminUsers() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [pwdUser, setPwdUser] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/users");
      setUsers(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`Removed ${u.email}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-users">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <span className="eyebrow">Team</span>
          <h1 className="mt-2 text-3xl text-navy flex items-center gap-3">
            <Users size={26} className="text-gold" /> Users &amp; Access
          </h1>
        </div>
        <button
          data-testid="user-add"
          onClick={() => setCreateOpen(true)}
          className="btn-gold"
        >
          <UserPlus size={16} /> Add sub-admin
        </button>
      </div>
      <span className="divider-gold" />

      <div className="card-elev overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-navy text-cream">
            <tr>
              <th className="text-start p-3">Name</th>
              <th className="text-start p-3">Email</th>
              <th className="text-start p-3">Role</th>
              <th className="text-start p-3">Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="p-6 text-center text-navy/50">
                <Loader2 className="animate-spin inline" /> Loading…
              </td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={5} className="p-6 text-center text-navy/50">No users yet.</td></tr>
            ) : users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-navy/10 hover:bg-cream">
                <td className="p-3 text-navy font-medium">
                  {u.name || <em className="text-navy/40">Unnamed</em>}
                  {u.id === currentUser?.id && (
                    <Badge className="ml-2 bg-emerald-100 text-emerald-800 text-[10px]">you</Badge>
                  )}
                </td>
                <td className="p-3 text-navy/80">{u.email}</td>
                <td className="p-3"><Badge className={ROLE_STYLE[u.role] || ""}>{u.role}</Badge></td>
                <td className="p-3 text-xs text-navy/60 whitespace-nowrap">
                  {new Date(u.created_at).toLocaleDateString()}
                </td>
                <td className="p-3 text-end whitespace-nowrap">
                  <button
                    data-testid={`user-edit-${u.id}`}
                    onClick={() => setEditUser(u)}
                    className="text-navy/70 hover:text-gold p-1.5"
                    title="Edit"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    data-testid={`user-pwd-${u.id}`}
                    onClick={() => setPwdUser(u)}
                    className="text-navy/70 hover:text-gold p-1.5"
                    title="Reset password"
                  >
                    <KeyRound size={14} />
                  </button>
                  <button
                    data-testid={`user-del-${u.id}`}
                    onClick={() => remove(u)}
                    disabled={u.id === currentUser?.id}
                    className="text-red-600/80 hover:text-red-700 p-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
                    title={u.id === currentUser?.id ? "Cannot delete yourself" : "Delete"}
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
        {ROLES.map((r) => (
          <div key={r} className="card-elev p-4">
            <Badge className={ROLE_STYLE[r]}>{r}</Badge>
            <div className="mt-2 text-navy/70">{ROLE_DESC[r]}</div>
          </div>
        ))}
      </div>

      {createOpen && (
        <CreateUserDialog onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); load(); }} />
      )}
      {editUser && (
        <EditUserDialog
          user={editUser}
          onClose={() => setEditUser(null)}
          onSaved={() => { setEditUser(null); load(); }}
        />
      )}
      {pwdUser && (
        <SetPasswordDialog
          user={pwdUser}
          onClose={() => setPwdUser(null)}
        />
      )}
    </div>
  );
}

function Modal({ children, onClose, testid }) {
  return (
    <div
      className="fixed inset-0 bg-navy/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="bg-white max-w-lg w-full rounded-md border border-gold/30" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        {children}
      </div>
    </div>
  );
}

function CreateUserDialog({ onClose, onCreated }) {
  const [f, setF] = useState({ email: "", name: "", role: "editor", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/users", f);
      toast.success(`Created ${f.email}`);
      onCreated();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} testid="user-create-modal">
      <form onSubmit={submit} className="p-8">
        <div className="flex justify-between items-start">
          <div>
            <span className="eyebrow">Team</span>
            <h2 className="mt-1 text-2xl text-navy">Add sub-admin</h2>
          </div>
          <button type="button" onClick={onClose} className="text-navy/50 hover:text-navy"><X size={18} /></button>
        </div>
        <span className="divider-gold" />

        <div className="space-y-4">
          <input data-testid="cu-name" className="field-box" placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          <input data-testid="cu-email" className="field-box" type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required />
          <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
            <SelectTrigger data-testid="cu-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <input data-testid="cu-password" className="field-box" type="password" placeholder="Initial password (8+ chars)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} minLength={8} required />
          <div className="text-xs text-navy/60 bg-cream p-3 rounded border border-gold/20">
            {ROLE_DESC[f.role]}
          </div>
        </div>

        <div className="mt-6 flex gap-3">
          <button data-testid="cu-submit" type="submit" disabled={busy} className="btn-gold disabled:opacity-40">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
            {busy ? "Creating…" : "Create user"}
          </button>
          <button type="button" onClick={onClose} className="btn-outline-navy">Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

function EditUserDialog({ user, onClose, onSaved }) {
  const [f, setF] = useState({ email: user.email, name: user.name || "", role: user.role });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/admin/users/${user.id}`, f);
      toast.success("Saved");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} testid="user-edit-modal">
      <form onSubmit={submit} className="p-8">
        <div className="flex justify-between items-start">
          <div>
            <span className="eyebrow">Edit</span>
            <h2 className="mt-1 text-2xl text-navy truncate">{user.email}</h2>
          </div>
          <button type="button" onClick={onClose} className="text-navy/50 hover:text-navy"><X size={18} /></button>
        </div>
        <span className="divider-gold" />

        <div className="space-y-4">
          <input data-testid="eu-name" className="field-box" placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          <input data-testid="eu-email" className="field-box" type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required />
          <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
            <SelectTrigger data-testid="eu-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="mt-6 flex gap-3">
          <button data-testid="eu-submit" type="submit" disabled={busy} className="btn-gold disabled:opacity-40">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Pencil size={14} />}
            {busy ? "Saving…" : "Save changes"}
          </button>
          <button type="button" onClick={onClose} className="btn-outline-navy">Cancel</button>
        </div>
      </form>
    </Modal>
  );
}

function SetPasswordDialog({ user, onClose }) {
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) { toast.error("Password must be at least 8 chars"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/users/${user.id}/set-password`, { new_password: next });
      toast.success(`Password reset for ${user.email}. Send them the new one securely.`);
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} testid="user-pwd-modal">
      <form onSubmit={submit} className="p-8">
        <div className="flex justify-between items-start">
          <div>
            <span className="eyebrow">Set password</span>
            <h2 className="mt-1 text-2xl text-navy truncate">{user.email}</h2>
          </div>
          <button type="button" onClick={onClose} className="text-navy/50 hover:text-navy"><X size={18} /></button>
        </div>
        <span className="divider-gold" />
        <input data-testid="pwd-new" className="field-box" type="password" placeholder="New password (8+ chars)" value={next} onChange={(e) => setNext(e.target.value)} minLength={8} required />
        <div className="mt-3 text-xs text-navy/60 bg-cream p-3 rounded border border-gold/20">
          The user&apos;s next login will use this password. Any pending reset link they may have will be invalidated.
        </div>
        <div className="mt-4 flex gap-3">
          <button data-testid="pwd-submit" type="submit" disabled={busy} className="btn-gold disabled:opacity-40">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
            {busy ? "Updating…" : "Set password"}
          </button>
          <button type="button" onClick={onClose} className="btn-outline-navy">Cancel</button>
        </div>
      </form>
    </Modal>
  );
}
